<template>
  <div>
    <Navbar>
      <div class="card">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h5 class="mb-0 font-weight-bold">Recomendaciones de Inventario</h5>
              <small class="text-muted">
                Sugerencias de compra generadas por el motor de análisis de demanda
              </small>
            </div>
            <div class="d-flex align-items-center">
              <input
                type="date"
                v-model="selectedDate"
                class="form-control form-control-sm mr-2"
                style="width: 160px;"
                :disabled="loading"
              />
              <button
                class="btn btn-primary btn-sm mr-2"
                @click="loadRecommendations"
                :disabled="loading"
              >
                <i class="fa fa-search mr-1"></i> Buscar
              </button>
              <button
                class="btn btn-outline-secondary btn-sm"
                @click="confirmRunAnalysis"
                :disabled="loading || running"
              >
                <i class="fa fa-refresh mr-1" :class="{ 'fa-spin': running }"></i>
                {{ running ? "Ejecutando..." : "Ejecutar Análisis" }}
              </button>
            </div>
          </div>

          <HorizontalLoader v-if="loading" />

          <div v-if="!loading && error" class="alert alert-danger">
            {{ error }}
          </div>

          <div v-if="!loading && runMessage" class="alert" :class="runSuccess ? 'alert-success' : 'alert-danger'">
            {{ runMessage }}
          </div>

          <div v-if="!loading && !error">
            <div v-if="recommendations.length === 0" class="text-center py-5 text-muted">
              <i class="fa fa-inbox fa-3x mb-3 d-block"></i>
              No hay recomendaciones de compra para la fecha seleccionada.
            </div>

            <div v-else>
              <p class="text-muted small mb-3">
                {{ recommendations.length }} producto(s) con sugerencias de compra para
                <strong>{{ selectedDate }}</strong>
              </p>
              <div class="table-responsive">
                <table class="table table-hover table-sm">
                  <thead class="thead-light">
                    <tr>
                      <th>Producto</th>
                      <th class="text-right">Stock Actual</th>
                      <th class="text-right">Demanda Diaria</th>
                      <th class="text-right">Demanda IA</th>
                      <th class="text-right">Qty Sugerida</th>
                      <th class="text-right">Qty Ajustada IA</th>
                      <th class="text-center">Riesgo</th>
                      <th class="text-right">Confianza</th>
                      <th>Motivo IA</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="rec in recommendations" :key="rec.Id">
                      <td class="font-weight-bold">{{ productName(rec.ProductId) }}</td>
                      <td class="text-right">{{ formatQty(rec.CurrentStock) }}</td>
                      <td class="text-right">{{ formatQty(rec.DemandDaily) }}</td>
                      <td class="text-right text-info">
                        {{ rec.DemandDailyAdjusted !== null ? formatQty(rec.DemandDailyAdjusted) : "—" }}
                      </td>
                      <td class="text-right font-weight-bold">{{ formatQty(rec.SuggestedQty) }}</td>
                      <td class="text-right font-weight-bold text-primary">
                        {{ rec.AdjustedSuggestedQty !== null ? formatQty(rec.AdjustedSuggestedQty) : "—" }}
                      </td>
                      <td class="text-center">
                        <span class="badge" :class="riskClass(rec.RiskLevel)">
                          {{ riskLabel(rec.RiskLevel) }}
                        </span>
                      </td>
                      <td class="text-right">{{ confidenceLabel(rec.Confidence) }}</td>
                      <td class="small text-muted" style="max-width: 220px; white-space: normal;">
                        {{ rec.AiReason || rec.Explanation || "—" }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Navbar>
  </div>
</template>

<script>
import Navbar from "../layout/Navbar.vue";
import HorizontalLoader from "../shared/HorizontalLoader.vue";
import { getRecommendations, runRecommendations } from "../../services/SmartInventoryService";
import { getProducts } from "../../services/ProductsService";

export default {
  name: "InventoryRecommendations",
  components: { Navbar, HorizontalLoader },
  data() {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return {
      selectedDate: yyyy + "-" + mm + "-" + dd,
      recommendations: [],
      productMap: {},
      loading: false,
      running: false,
      error: null,
      runMessage: null,
      runSuccess: false,
    };
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      this.error = null;
      try {
        const companyId = this.$store.getters.getCompany;
        const [recsRes, prodsRes] = await Promise.all([
          getRecommendations(companyId, this.selectedDate),
          getProducts(companyId, null, null, 1, 1000, 1),
        ]);

        if (recsRes && recsRes.ok) {
          this.recommendations = recsRes.data || [];
        } else {
          this.error = (recsRes && recsRes.message) || "Error al cargar recomendaciones";
        }

        const products = (prodsRes && prodsRes.data && prodsRes.data.products) || [];
        const map = {};
        products.forEach(function(p) {
          map[p.Id] = p.Name;
        });
        this.productMap = map;
      } catch (e) {
        this.error = "Error inesperado al cargar los datos";
      } finally {
        this.loading = false;
      }
    },
    loadRecommendations() {
      this.runMessage = null;
      this.loadData();
    },
    confirmRunAnalysis() {
      this.$helpers.confirm("¿Deseas ejecutar el análisis de inventario ahora? Esto procesará las ventas del día anterior y generará nuevas recomendaciones.")
        .then((ok) => {
          if (ok) this.runAnalysis();
        });
    },
    async runAnalysis() {
      this.running = true;
      this.runMessage = null;
      this.runSuccess = false;
      try {
        const companyId = this.$store.getters.getCompany;
        const res = await runRecommendations(companyId);
        if (res && res.ok) {
          this.runMessage = "Análisis ejecutado correctamente. Las recomendaciones se actualizarán en breve.";
          this.runSuccess = true;
          setTimeout(() => { this.loadData(); }, 1500);
        } else {
          this.runMessage = (res && res.message) || "Error al ejecutar el análisis";
          this.runSuccess = false;
        }
      } catch (e) {
        this.runMessage = "Error inesperado al ejecutar el análisis";
        this.runSuccess = false;
      } finally {
        this.running = false;
      }
    },
    productName(productId) {
      return this.productMap[productId] || ("Producto #" + productId);
    },
    formatQty(val) {
      if (val === null || val === undefined) return "0";
      return parseFloat(val).toLocaleString("es-DO", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    riskClass(level) {
      if (level === "high") return "badge-danger";
      if (level === "medium") return "badge-warning";
      if (level === "low") return "badge-success";
      return "badge-secondary";
    },
    riskLabel(level) {
      if (level === "high") return "Alto";
      if (level === "medium") return "Medio";
      if (level === "low") return "Bajo";
      return "—";
    },
    confidenceLabel(val) {
      if (val === null || val === undefined) return "—";
      return Math.round(parseFloat(val) * 100) + "%";
    },
  },
};
</script>
